import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { CoverImage } from '@/components/CoverImage';
import { Clock, CheckCircle, XCircle, BookOpen } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface FocusSession {
  id: string;
  book_id: string | null;
  duration_minutes: number;
  actual_duration_seconds: number;
  was_completed: boolean;
  started_at: string;
  books?: {
    title: string;
    authors: string[];
    cover_url: string | null;
  } | null;
}

export function SessionHistory() {
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['focus-sessions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('focus_sessions')
        .select(`
          id,
          book_id,
          duration_minutes,
          actual_duration_seconds,
          was_completed,
          started_at,
          books (
            title,
            authors,
            cover_url
          )
        `)
        .eq('user_id', user.id)
        .order('started_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as FocusSession[];
    },
  });

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-muted/50 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card className="p-8 text-center">
        <BookOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
        <h3 className="font-semibold text-foreground mb-2">No sessions yet</h3>
        <p className="text-sm text-muted-foreground">
          Start your first focus session to see it here
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {sessions.map((session) => (
        <Card key={session.id} className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            {/* Book cover */}
            {session.books ? (
              <CoverImage
                bookId={session.book_id || ''}
                title={session.books.title}
                author={session.books.authors?.[0]}
                coverUrl={session.books.cover_url}
                size="xs"
                showPlaceholder={true}
              />
            ) : (
              <div className="w-12 h-16 rounded bg-muted flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-muted-foreground" />
              </div>
            )}

            {/* Session info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-foreground truncate">
                {session.books?.title || 'General Focus'}
              </h4>
              <p className="text-sm text-muted-foreground truncate">
                {session.books?.authors?.join(', ') || 'No book selected'}
              </p>
              <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(session.actual_duration_seconds)}
                </span>
                <span>
                  {formatDistanceToNow(new Date(session.started_at), { addSuffix: true })}
                </span>
              </div>
            </div>

            {/* Completion status */}
            {session.was_completed ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
